/**
 * Single source of truth for chat LLM providers and their per-provider
 * feature support (PLT-5021 / PLT-5034).
 *
 * This lives in `src/common/` — shared by the backend chat handler AND the
 * frontend chat UI — on purpose. The backend ENFORCES these capabilities
 * server-side (see `chat/index.ts`: web search forced off, attachments sent
 * inline instead of via the US Files API, token-preview skipped on a
 * non-`anthropic` provider), and the UI READS the same table to hide affordances the active provider
 * can't serve (PLT-5034). Keeping one table means the UI can never advertise
 * a feature the backend will refuse, and adding a provider is a single row
 * here rather than a change in two reporganised places.
 *
 * The backend `chat/llm-provider.ts` imports `ChatProvider` / `CAPABILITIES`
 * / `getCapabilities` from here and adds the SDK-bound pieces it can't put in
 * `common` (the `AnthropicBedrock` client construction, the AWS region map,
 * the `eu.anthropic.*` inference-profile id mapping, the privilege-gated
 * `resolveChatProvider`). This file deliberately has ZERO runtime
 * dependencies (pure types + plain-object data) so it never drags a server
 * SDK into the UI bundle — same contract as `citation-types.ts` in this
 * directory.
 */

/**
 * Where Claude inference runs for a chat request:
 *
 *   - `'anthropic'`  — the default: Anthropic's hosted API in the US. What
 *                      every existing user gets today.
 *   - `'bedrock-eu'` — Claude on AWS Bedrock in eu-west-1 (`eu.anthropic.*`
 *                      inference profiles), for customers who require EU data
 *                      residency. Used by every chat of a non-team user who
 *                      holds the (default-off) `USE_AI_CHAT_EU_RESIDENCY`
 *                      privilege, and by team users who turn on the staff EU
 *                      testing switch (PLT-7018).
 */
export type ChatProvider = 'anthropic' | 'bedrock-eu';

/** Provider used when a request neither opts in to nor qualifies for EU residency. */
export const DEFAULT_CHAT_PROVIDER: ChatProvider = 'anthropic';

/**
 * All valid `ChatProvider` values. The single list the backend Joi payload
 * schema (`payload-validation.ts`) and the UI selectors validate against, so
 * the wire contract and the `ChatProvider` union can never drift. Keep in
 * sync with the union above.
 */
export const CHAT_PROVIDERS: readonly ChatProvider[] = ['anthropic', 'bedrock-eu'];

/** Narrow an arbitrary string to a known `ChatProvider`. */
export function isChatProvider(value: string | undefined): value is ChatProvider {
  return value !== undefined && (CHAT_PROVIDERS as readonly string[]).includes(value);
}

/**
 * How web search is provided for a given provider. Web search is a capability
 * with two DIFFERENT backing implementations rather than a plain on/off flag:
 * the US `'anthropic'` path uses Anthropic's server-side `web_search_*` tool,
 * while the EU Bedrock path has no such server tool and instead uses our own
 * client-executed `web_search` / `fetch_page` tools backed by ScrapingBee
 * (PLT-5456). `false` means unavailable. The backend branches on the value to
 * decide which tool to register; the UI only reads its truthiness to show/hide
 * the "Web access" pill, so this union is a drop-in for the former boolean.
 */
export type WebSearchImpl = false | 'anthropic-server' | 'scrapingbee';

/**
 * How code execution is provided. `'anthropic-server'` is Anthropic's
 * server-side sandboxed container (US path); `'agentcore'` is AWS Bedrock
 * AgentCore Code Interpreter in eu-west-1, which keeps code and data in-region
 * for the EU path (PLT-5453); `false` means unavailable. Same discriminator
 * pattern as `WebSearchImpl`.
 */
export type CodeExecutionImpl = false | 'anthropic-server' | 'agentcore';

/**
 * What an LLM provider supports. Each flag maps 1:1 onto an optional request
 * feature the backend gates today, so adding a provider is a matter of
 * filling in this row.
 */
export interface ProviderCapabilities {
  /**
   * Whether the composer accepts file attachments AT ALL for this provider —
   * the "Add files" pill in the UI and the backend's keep/drop decision both
   * gate off this flag. It is independent of HOW the bytes reach the model:
   *
   *   - On `'anthropic'` attachments go through the US Files API (see
   *     `fileUpload`), with an inline-base64 fallback if that upload fails.
   *   - On `'bedrock-eu'` there is no Files API, so attachments are sent as
   *     inline base64 `document` / `image` content blocks in each request
   *     (`attachmentToInlineContentBlock`). The bytes are part of the request
   *     payload for that single inference call and are not persisted —
   *     consistent with EU-mode data handling. The one type the inline path
   *     can't carry is DOCX (Anthropic's inline `document` source accepts only
   *     PDF + plaintext); the backend rejects it with a clear upload-error
   *     rather than 400ing the stream mid-turn.
   *
   * `true` for every provider today — both can accept attachments — but kept
   * as its own flag (rather than folded into `fileUpload`) so a future provider
   * that genuinely can't take files flips one row here.
   */
  attachments: boolean;
  /**
   * Use the US Anthropic Files API (upload → `file_id`, referenced across the
   * turn) AND send the `files-api` beta header. This reaches a US-only Anthropic
   * surface, so it is `false` on `'bedrock-eu'` — that path still accepts
   * attachments (see `attachments`) but sends them inline instead. When true,
   * `attachments` is necessarily true as well.
   */
  fileUpload: boolean;
  /**
   * Which web-search implementation this provider uses (`'anthropic-server'`,
   * `'scrapingbee'`, or `false` for none). See `WebSearchImpl`. The backend
   * branches on the value in `buildChatTools` / `buildChatSystemText`; the UI
   * reads only its truthiness to show/hide the "Web access" pill. Whether it
   * starts on or off is `webSearchOffAtChatStart`.
   */
  webSearch: WebSearchImpl;
  /**
   * Web search starts off in every chat, and runs only when the user has turned
   * it on for this provider (PLT-7271). `true` where web search sends search
   * terms outside the region the provider keeps the chat in. The UI resets the
   * choice at every chat start and sends the provider the choice was made for
   * as `webSearchProvider`; the backend turns web search on only when that
   * matches the provider it resolved (`chooseWebSearchImpl`), so a browser that
   * guessed the provider wrong cannot turn it on.
   */
  webSearchOffAtChatStart: boolean;
  /**
   * Which code-execution implementation this provider uses
   * (`'anthropic-server'`, `'agentcore'`, or `false` for none). See
   * `CodeExecutionImpl`. Note the `code-execution-2025-08-25` beta header is
   * sent ONLY for `'anthropic-server'` — the EU Bedrock profiles reject unknown
   * beta flags (PLT-5107), and the AgentCore path is a separate AWS API, not an
   * Anthropic beta.
   */
  codeExecution: CodeExecutionImpl;
  /** Extended ("adaptive") thinking. */
  extendedThinking: boolean;
  /** `output_config.effort` level. */
  effort: boolean;
  /** Prompt caching (`cache_control` markers, incl. the 1h tier). */
  promptCaching: boolean;
  /**
   * The `fine-grained-tool-streaming-2025-05-14` Anthropic beta header, which
   * streams `input_json_delta` events progressively instead of buffering them
   * per-parameter. It is sent as an `anthropic-beta` request header on the chat
   * stream call. The `eu.anthropic.*` Bedrock inference profiles reject unknown
   * beta flags with `400 invalid beta flag` (PLT-5107), and this flag is an
   * unconfirmed pass-through there, so it is `false` on `'bedrock-eu'`. Turning
   * it off only changes streaming granularity — tool calls still work, the
   * input just arrives in fewer, larger deltas.
   */
  fineGrainedToolStreaming: boolean;
  /**
   * Native citations on Upright data-tool results: `index.ts` wraps citable
   * tool results in Anthropic `search_result` content blocks
   * (`buildCitableToolResultContent`) and sends the paired
   * `search-results-2025-06-09` beta header. The header and the blocks are a
   * package deal — emitting the blocks without the beta (or vice versa) is
   * itself a request error. The EU Bedrock profiles reject the beta flag
   * (PLT-5107), so this is `false` on `'bedrock-eu'`: tool results fall back to
   * the plain stringified JSON content and the answer simply carries no inline
   * citation pills. Re-enable once the EU profiles confirm `search-results`
   * support.
   *
   * `false` here does NOT mean the provider has no citation path. Document
   * citations are a separate, GA mechanism that needs no beta header, and they
   * work on `'bedrock-eu'`: a PDF sent as an inline `document` block with
   * `citations: { enabled: true }` (`chat/anthropic-files.ts`) grounds real
   * `page_location` citations, confirmed against the live EU inference
   * profiles (PLT-6420). What the EU path lacks is grounding on tool results
   * and on web search (PLT-5456) — which SOURCES can be cited, not whether
   * citations work at all.
   */
  toolResultCitations: boolean;
  /**
   * The team-only `/api/chat/preview-tokens` token-count preview. It calls
   * Anthropic's `countTokens` against the US API (and `AnthropicBedrock`
   * exposes no `countTokens` surface at all), so it ships the live
   * conversation to a US endpoint — a residency leak on the EU path. The UI
   * hides the preview panel when this is `false` (PLT-5034) and the backend
   * fails closed in `preview-tokens.ts`.
   */
  tokenPreview: boolean;
}

/**
 * Single source of truth for per-provider feature support.
 *
 * Note on `'bedrock-eu'`: the US Files API (`fileUpload`) and the US
 * `countTokens` preview do not exist on the `eu.anthropic.*` Bedrock path, so
 * they are `false`. Anthropic's SERVER-side web search and code execution also
 * don't exist there — but rather than dropping the capabilities entirely, the
 * EU path provides its OWN implementations: `webSearch: 'scrapingbee'`
 * (custom `web_search` / `fetch_page` tools, PLT-5456) and
 * `codeExecution: 'agentcore'` (AWS Bedrock AgentCore Code Interpreter in
 * eu-west-1, PLT-5453). Only code execution stays in-region: ScrapingBee is a
 * third-party service that sends the search terms on to Google, both outside
 * the EU (PLT-7271). Attachments are supported
 * (`attachments: true`) and sent as inline base64 `document` / `image` content
 * blocks rather than via the Files API. The two `anthropic-beta` header flags
 * the chat stream sends — `fineGrainedToolStreaming` and `toolResultCitations`
 * (the `search-results` block citations) — are rejected by the EU Bedrock
 * profiles with `400 invalid beta flag` (PLT-5107), so they are `false` there;
 * EU web-search results are cited as plain inline markdown links instead.
 * Citations on uploaded documents are a separate mechanism and do work on the
 * EU path — see `toolResultCitations` for what that `false` does and does not
 * cover.
 * Extended thinking, effort, and the 1h prompt-cache tier are expected to pass
 * through but are still to be confirmed against the live EU profiles (PLT-5037
 * item c); they are left `true` here and should be corrected if confirmation
 * shows otherwise — this table is where that correction belongs.
 */
export const CAPABILITIES: Record<ChatProvider, ProviderCapabilities> = {
  anthropic: {
    attachments: true,
    fileUpload: true,
    webSearch: 'anthropic-server',
    webSearchOffAtChatStart: false,
    codeExecution: 'anthropic-server',
    extendedThinking: true,
    effort: true,
    promptCaching: true,
    fineGrainedToolStreaming: true,
    toolResultCitations: true,
    tokenPreview: true,
  },
  'bedrock-eu': {
    // Attachments ARE supported on the EU path — sent inline (see `attachments`
    // doc), not via the US Files API (`fileUpload: false`).
    attachments: true,
    fileUpload: false,
    // PLT-5456: web search via our own ScrapingBee-backed `web_search` /
    // `fetch_page` tools (Anthropic's server tool doesn't exist on Bedrock).
    // Leaves the EU, so it starts off in every chat (PLT-7271).
    webSearch: 'scrapingbee',
    webSearchOffAtChatStart: true,
    // PLT-5453: code execution via AWS Bedrock AgentCore Code Interpreter
    // (eu-west-1), keeping code/data in-region. Custom `execute_python` tool,
    // not Anthropic's server container.
    codeExecution: 'agentcore',
    extendedThinking: true,
    effort: true,
    promptCaching: true,
    fineGrainedToolStreaming: false,
    toolResultCitations: false,
    tokenPreview: false,
  },
};

export function getCapabilities(provider: ChatProvider): ProviderCapabilities {
  return CAPABILITIES[provider];
}
